import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { OrdemDeServicoCompleta } from '../types/ordemServico';
import { supabase } from '../lib/supabase';
import { pessoaService } from './pessoaService';
import { buscarModeloAtivo, gerarPdfDeModelo } from './modeloRendererService';

export async function generateOsPdf(os: OrdemDeServicoCompleta): Promise<void> {
  // Format Date DD/MM/YYYY
  const formatDate = (dateStr?: string) => {
    if (!dateStr) {
      const today = new Date();
      return today.toLocaleDateString('pt-BR');
    }
    try {
      const cleanDate = dateStr.split('T')[0];
      const parts = cleanDate.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  // Format Time HH:mm:ss
  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '';
    const clean = timeStr.trim();
    const parts = clean.split(':');
    if (parts.length === 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:00`;
    }
    if (parts.length === 3) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:${parts[2].padStart(2, '0')}`;
    }
    return clean;
  };

  // Calculate duration string HH:mm:ss
  const calculateTotalHours = (inicio?: string, fim?: string, totalInformado?: string) => {
    if (totalInformado && totalInformado.trim()) {
      return formatTime(totalInformado);
    }
    if (!inicio || !fim) return '';
    try {
      const [h1, m1] = inicio.split(':').map(Number);
      const [h2, m2] = fim.split(':').map(Number);
      if (isNaN(h1) || isNaN(m1) || isNaN(h2) || isNaN(m2)) return '';
      let diffMins = (h2 * 60 + m2) - (h1 * 60 + m1);
      if (diffMins < 0) diffMins += 24 * 60;
      const hrs = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
    } catch {
      return '';
    }
  };

  // Format Currency
  const formatCurrency = (val?: number | string | null): string => {
    if (val === undefined || val === null || val === '') return 'R$ ';
    const num = typeof val === 'number' ? val : parseFloat(String(val).replace(',', '.'));
    if (isNaN(num)) return 'R$ ';
    return `R$ ${num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // 1. Cliente Data Lookup
  let clienteNome = os.pedido?.pessoa?.nome || os.cliente_nome || '';
  let clienteEmail = '';
  let clienteTelefone = '';
  let clienteDocumento = '';
  let solicitanteNome = '';
  let pessoaId = os.pedido?.pessoa_id || os.pedido?.pessoa?.id;

  try {
    if (!clienteNome && os.pedido_id) {
      const { data: pedData } = await supabase
        .from('pedido')
        .select('*, pessoa:pessoa_id(*)')
        .eq('id', os.pedido_id)
        .maybeSingle();
      if (pedData) {
        pessoaId = pedData.pessoa_id;
        clienteNome = pedData.pessoa?.nome || pedData.pessoa?.nome_fantasia || '';
      }
    }

    if (pessoaId) {
      const { data: pData } = await supabase
        .from('pessoa')
        .select('*')
        .eq('id', pessoaId)
        .maybeSingle();

      if (pData) {
        if (!clienteNome) clienteNome = pData.nome || pData.nome_fantasia || '';
        clienteEmail = pData.email || '';
        clienteTelefone = pData.fone || '';
        clienteDocumento = pData.documento || '';
      }

      const contatos: any[] = await pessoaService.getContatosByPessoa(pessoaId);
      if (contatos && contatos.length > 0) {
        const principal = contatos.find(c => c.principal === 'S' || c.principal === 'Sim') || contatos[0];
        if (principal.email) clienteEmail = principal.email;
        if (principal.telefone || principal.celular) clienteTelefone = principal.telefone || principal.celular;
        solicitanteNome = principal.nome || '';
      }
    }
  } catch (err) {
    console.error('Erro ao buscar cliente do PDF:', err);
  }

  // 2. Motorista & Veículo Lookup
  let motoristaNome = os.motorista_nome || os.veiculos?.motorista?.nome || os.veiculos?.agregado?.nome || '';
  let motoristaDocumento = '';
  let placa = os.veiculos?.placa || (os as any).placa || '';
  let motoristaId = os.veiculos?.motorista?.id || (os as any).motorista_id || (os as any).agregado;

  try {
    if (os.veiculos_id && (!placa || !motoristaNome)) {
      const { data: vData } = await supabase
        .from('veiculos')
        .select('*, motorista:motorista_id(*), agregado:agregado_id(*)')
        .eq('id', os.veiculos_id)
        .maybeSingle();

      if (vData) {
        if (!placa) placa = vData.placa || '';
        if (!motoristaNome) {
          motoristaNome = vData.motorista?.nome || vData.agregado?.nome || '';
        }
        if (!motoristaId) {
          motoristaId = vData.motorista_id || vData.agregado_id;
        }
      }
    }

    if (motoristaId) {
      const { data: mData } = await supabase
        .from('pessoa')
        .select('*')
        .eq('id', motoristaId)
        .maybeSingle();

      if (mData) {
        if (!motoristaNome) motoristaNome = mData.nome || mData.nome_fantasia || '';
        motoristaDocumento = mData.documento || '';
      }
    }
  } catch (err) {
    console.error('Erro ao buscar motorista do PDF:', err);
  }

  // 3. Tipo Veículo Lookup
  let tipoVeiculoNome = os.tipo_veiculo?.tipo_veiculo || '';
  if (!tipoVeiculoNome && os.tipo_veiculo_id) {
    try {
      const { data: tvData } = await supabase
        .from('tipo_veiculo')
        .select('tipo_veiculo')
        .eq('id', os.tipo_veiculo_id)
        .maybeSingle();
      if (tvData) {
        tipoVeiculoNome = tvData.tipo_veiculo;
      }
    } catch (err) {
      console.error('Erro ao buscar tipo de veículo:', err);
    }
  }

  // Fallbacks
  if (!tipoVeiculoNome) tipoVeiculoNome = 'HR ATÉ 1.500KG - CAPITAL';
  if (!clienteNome) clienteNome = 'MUNTE - CONDOMÍNIO SHOPPING CENTER IBIRAPUERA';

  // Valores
  const valorVeiculo = formatCurrency(os.valor_unit_servi || os.total_os);
  const ajudanteNome = os.fk_item_ajudante?.servicos || (os as any).item_ajudante || '';
  const valorAjudanteVal = os.valor_unit_ajud;
  const valorAjudante = valorAjudanteVal ? formatCurrency(valorAjudanteVal) : 'R$ ';
  const qtdAjudante = os.quantidade_ajudante ? String(os.quantidade_ajudante) : '';
  const subtotalAjudanteVal = (Number(os.valor_unit_ajud) || 0) * (Number(os.quantidade_ajudante) || 0);
  const subtotalAjudante = subtotalAjudanteVal ? formatCurrency(subtotalAjudanteVal) : 'R$ ';

  // Times & Duration
  const horaInicial = formatTime(os.horario_inicio);
  const horaFinal = formatTime(os.horario_final);
  const totalHoras = calculateTotalHours(os.horario_inicio, os.horario_final, os.hora_total);

  // Addresses & Description
  const enderecoRetirada = (os.retirada || '').toUpperCase();
  const enderecoEntrega = (os.entrega || '').toUpperCase();
  const descricaoServicos = os.descricao || '';

  // Total
  const valorTotalOS = formatCurrency(os.total_os || os.valor_unit_servi);

  // Security Signature fields
  const rgAssinatura = os.rg_assinatura || '';
  const ipAssinatura = os.ip_assinatura || '';

  // Data da OS
  const dataOs = formatDate(os.data_atendimento || os.created_at);

  // Se existir um modelo de "Ordem de Serviço" ativo no Gerenciador de Modelos,
  // ele é usado para gerar o PDF (é o que o usuário desenhou/editou lá).
  // Sem modelo ativo, cai no layout fixo abaixo (comportamento de sempre).
  const modeloAtivo = await buscarModeloAtivo('ordem_servico');
  if (modeloAtivo) {
    // Status legível (a OS guarda só o status_id)
    let statusServico = '';
    try {
      if (os.status_id) {
        const { data: stData } = await supabase
          .from('status')
          .select('status')
          .eq('id', os.status_id)
          .maybeSingle();
        statusServico = stData?.status || '';
      }
    } catch {
      statusServico = '';
    }

    const dadosOs: Record<string, string> = {
      NUMERO_OS: String(os.id || ''),
      DATA_EMISSAO: dataOs,
      STATUS_SERVICO: statusServico,
      SOLICITANTE: solicitanteNome,
      TELEFONE_SOLICITANTE: clienteTelefone,

      CLIENTE_NOME: clienteNome.toUpperCase(),
      CLIENTE_EMAIL: clienteEmail,
      CLIENTE_TELEFONE: clienteTelefone,
      CLIENTE_DOCUMENTO: clienteDocumento,
      ENDERECO_COLETA: enderecoRetirada,
      ENDERECO_ENTREGA: enderecoEntrega,

      VEICULO_TIPO: tipoVeiculoNome.toUpperCase(),
      VEICULO_PLACA: placa.toUpperCase(),
      MOTORISTA_NOME: motoristaNome.toUpperCase(),
      MOTORISTA_DOCUMENTO: motoristaDocumento,
      AJUDANTE_NOME: ajudanteNome.toUpperCase(),
      QTD_AJUDANTE: qtdAjudante,
      OBSERVACOES: (os as any).detalhes_adicional || (os as any).ocorrencias || '',
      SERVICO_DESCRICAO: descricaoServicos,

      VALOR_FRETE: valorVeiculo.replace('R$', '').trim(),
      VALOR_AJUDANTE: valorAjudante.replace('R$', '').trim(),
      SUBTOTAL_AJUDANTE: subtotalAjudante.replace('R$', '').trim(),
      VALOR_TAXAS: formatCurrency((os as any).valor_despesa).replace('R$', '').trim(),
      VALOR_TOTAL: valorTotalOS.replace('R$', '').trim(),

      HORA_INICIAL: horaInicial,
      HORA_FINAL: horaFinal,
      TOTAL_HORAS: totalHoras,

      ASSINATURA: os.assinatura || '',
      RG_ASSINATURA: String(rgAssinatura),
      IP_ASSINATURA: String(ipAssinatura),
      DATA_ASSINATURA: os.assinatura
        ? `${formatDate(os.updated_at || os.data_atendimento)} ${formatTime((os.updated_at || '').split('T')[1]?.slice(0, 5))}`.trim()
        : '',
      VALIDACAO_DIGITAL: os.assinatura ? 'Documento Validado via login e senha.' : '',
    };

    try {
      await gerarPdfDeModelo(modeloAtivo, dadosOs, `Ordem_de_Servico_#${os.id}.pdf`);
      return;
    } catch (err) {
      console.error('Erro ao gerar PDF a partir do modelo customizado, usando layout padrão embutido:', err);
      // segue para o fallback abaixo
    }
  }

  // Create DOM container formatted identically to the user's PDF reference image
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  container.style.width = '794px'; // Standard A4 width at 96 DPI
  container.style.minHeight = '1123px'; // Standard A4 height at 96 DPI
  container.style.backgroundColor = '#ffffff';
  container.style.boxSizing = 'border-box';
  container.style.padding = '40px 50px';
  container.style.display = 'flex';
  container.style.flexDirection = 'column';
  container.style.justifyContent = 'space-between';
  container.style.fontFamily = 'Arial, Helvetica, sans-serif';
  container.style.color = '#000000';

  container.innerHTML = `
    <div style="width: 100%; display: flex; flex-direction: column;">
      
      <!-- HEADER -->
      <div style="display: flex; justify-content: space-between; align-items: flex-start; width: 100%;">
        <!-- VOOPE LOGO -->
        <div style="display: flex; align-items: center; gap: 8px;">
          <svg width="46" height="46" viewBox="0 0 100 100" fill="none" style="margin-right: 2px;">
            <path d="M8 12 L40 88 L56 88 L24 12 Z" fill="#DC2626" />
            <path d="M36 12 L68 88 L84 88 L52 12 Z" fill="#000000" />
          </svg>
          <div style="display: flex; flex-direction: column;">
            <span style="font-size: 26px; font-weight: 900; color: #000000; font-family: Arial, sans-serif; letter-spacing: 0.5px; line-height: 1;">VOOPE</span>
            <span style="font-size: 7.5px; font-weight: bold; color: #000000; font-family: Arial, sans-serif; letter-spacing: 1.2px; margin-top: 2px;">TRANSPORTE E CARGAS</span>
          </div>
        </div>

        <!-- CONTACT INFO -->
        <div style="text-align: right; font-family: Arial, sans-serif; font-size: 12.5px; line-height: 1.45;">
          <div style="font-weight: bold; font-size: 13.5px; color: #000000; letter-spacing: 0.3px;">VOOPE TRANSPORTES</div>
          <div style="margin-top: 3px;">
            <strong>TELEFONE:</strong> <span style="color: #0002ff; text-decoration: underline;">(11) 9 - 4711-5843</span>
          </div>
          <div>
            <span style="color: #0002ff; text-decoration: underline;">comercial@voopetrans.com.br</span>
          </div>
        </div>
      </div>

      <!-- SEPARATOR -->
      <div style="width: 100%; height: 1.5px; background-color: #000000; margin: 12px 0 10px 0;"></div>

      <!-- TITLE -->
      <div style="font-size: 13.5px; font-weight: bold; color: #000000; text-align: center; font-family: Arial, sans-serif;">
        Ordem de Serviço: ${os.id || ''} - Data: ${dataOs}
      </div>

      <!-- SEPARATOR -->
      <div style="width: 100%; height: 1.5px; background-color: #000000; margin: 10px 0 12px 0;"></div>

      <!-- CLIENT BLOCK -->
      <div style="font-size: 12.5px; color: #000000; font-family: Arial, sans-serif; line-height: 1.65;">
        <div><strong>Cliente:</strong> ${clienteNome.toUpperCase()}</div>
        <div><strong>E-mail:</strong> ${clienteEmail}</div>
        <div><strong>Telefone:</strong> ${clienteTelefone}</div>
      </div>

      <!-- SEPARATOR -->
      <div style="width: 100%; height: 1.5px; background-color: #000000; margin: 12px 0;"></div>

      <!-- VEHICLE & HELPERS BLOCK -->
      <div style="font-size: 12.5px; color: #000000; font-family: Arial, sans-serif; line-height: 1.65;">
        <div><strong>Veiculo Solicitado:</strong> ${tipoVeiculoNome.toUpperCase()}</div>
        <div style="margin-top: 2px;"><strong> Valor:</strong> ${valorVeiculo}</div>
        <div style="margin-top: 2px;">
          <strong>Ajudante:</strong> ${ajudanteNome.toUpperCase()} &nbsp;&nbsp;&nbsp;&nbsp;
          <strong>Valor:</strong> ${valorAjudante} &nbsp;&nbsp;&nbsp;&nbsp;
          <strong>Quantidade:</strong> ${qtdAjudante} &nbsp;&nbsp;&nbsp;&nbsp;
          <strong>Sub Total:</strong> ${subtotalAjudante}
        </div>
      </div>

      <!-- SEPARATOR -->
      <div style="width: 100%; height: 1.5px; background-color: #000000; margin: 12px 0;"></div>

      <!-- TIMINGS -->
      <div style="font-size: 12.5px; color: #000000; font-family: Arial, sans-serif; margin-bottom: 16px;">
        <strong>Hora Inicial:</strong> ${horaInicial} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        <strong>Hora Final:</strong> ${horaFinal} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        <strong>Total de Horas:</strong> ${totalHoras}
      </div>

      <!-- ADDRESSES & DESCRIPTION -->
      <div style="font-size: 12.5px; color: #000000; font-family: Arial, sans-serif; line-height: 1.6;">
        <div style="margin-bottom: 14px;">
          <strong>Endereço de Retirada:</strong><br>
          <div style="margin-top: 2px;">${enderecoRetirada}</div>
        </div>
        
        <div style="margin-bottom: 14px;">
          <strong>Endereço de Entrega:</strong><br>
          <div style="margin-top: 2px;">${enderecoEntrega}</div>
        </div>

        <div style="margin-bottom: 6px;">
          <strong>Descrição Dos Serviços:</strong><br>
          <div style="margin-top: 2px;">${descricaoServicos}</div>
        </div>
      </div>

      <!-- SEPARATOR -->
      <div style="width: 100%; height: 1.5px; background-color: #000000; margin: 12px 0;"></div>

      <!-- DRIVER & PLATE -->
      <div style="font-size: 12.5px; color: #000000; font-family: Arial, sans-serif; line-height: 1.65;">
        <div>
          <strong>Motorista:</strong> ${motoristaNome.toUpperCase()} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <strong>Documento:</strong> ${motoristaDocumento}
        </div>
        <div style="margin-top: 2px;">
          <strong>Placa:</strong> ${placa.toUpperCase()}
        </div>
      </div>

      <!-- SEPARATOR -->
      <div style="width: 100%; height: 1.5px; background-color: #000000; margin: 12px 0;"></div>

      <!-- TOTAL OS VALUE -->
      <div style="font-size: 13px; font-weight: bold; color: #000000; font-family: Arial, sans-serif;">
        Valor Total O.S: ${valorTotalOS}
      </div>

      <!-- SEPARATOR -->
      <div style="width: 100%; height: 1.5px; background-color: #000000; margin: 12px 0;"></div>

      <!-- SIGNATURE SPACE -->
      <div style="font-size: 12.5px; color: #000000; font-family: Arial, sans-serif; min-height: 80px;">
        <div><strong>Assinatura:</strong></div>
        ${os.assinatura ? `
          <div style="margin-top: 6px; display: flex; align-items: center; height: 60px;">
            <img src="${os.assinatura}" style="max-height: 60px; max-width: 300px; object-fit: contain;" referrerPolicy="no-referrer" />
          </div>
        ` : '<div style="height: 50px;"></div>'}
      </div>

    </div>

    <!-- SECURITY FOOTER -->
    <div style="width: 100%; display: flex; flex-direction: column; align-items: center;">
      <!-- SEPARATOR -->
      <div style="width: 100%; height: 1.5px; background-color: #000000; margin: 10px 0;"></div>

      <div style="font-family: Arial, sans-serif; text-align: center; font-size: 12.5px; color: #000000; line-height: 1.6;">
        <div><strong>RG:</strong> ${rgAssinatura}</div>
        <div><strong>IP:</strong> ${ipAssinatura}</div>
        <div style="margin-top: 16px; font-weight: bold; font-size: 13px;">Documento Validado via login e senha.</div>
      </div>
    </div>
  `;

  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2.5,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: true,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const imgWidth = 210;
    const imgHeight = 297;

    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');
    pdf.save(`Ordem_de_Servico_#${os.id}.pdf`);
  } catch (err) {
    console.error('Erro ao gerar PDF da Ordem de Serviço:', err);
    alert('Não foi possível gerar o PDF da Ordem de Serviço. Tente novamente.');
  } finally {
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
  }
}
